import { useEffect, useMemo, useRef, useState } from 'react';
import { MdArrowBack, MdLock, MdLockReset, MdOutlineVisibility, MdOutlineVisibilityOff, MdPhone } from 'react-icons/md';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../../axios';
import styles from './ResetPassword.module.css';

const OTP_LENGTH = 6;

const normalizePhone = (value) => {
    let phone = String(value || '')
        .replace(/[^\d+]/g, '')
        .trim();

    if (!phone) return '';

    if (phone.startsWith('+')) phone = phone.substring(1);
    if (phone.startsWith('0')) phone = `62${phone.substring(1)}`;
    else if (phone.startsWith('8')) phone = `62${phone}`;

    return phone;
};

const maskLogin = (value = '') => {
    const login = String(value).trim();

    if (!login) return '-';

    if (login.includes('@')) {
        const [name, domain] = login.split('@');
        return `${name.slice(0, 2)}***@${domain}`;
    }

    const normalized = normalizePhone(login);

    if (normalized.length < 8) return login;

    return `${normalized.slice(0, 4)}****${normalized.slice(-3)}`;
};

const ResetPassword = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const inputRefs = useRef([]);

    const login = useMemo(() => {
        return location.state?.login || sessionStorage.getItem('mp_reset_login') || '';
    }, [location.state]);

    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
    const [form, setForm] = useState({
        password: '',
        password_confirmation: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({
        otp: '',
        password: '',
        password_confirmation: '',
    });

    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);

    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;

        const digit = value.slice(-1);

        setOtp((prev) => {
            const next = [...prev];
            next[index] = digit;
            return next;
        });

        setError('');
        setFieldErrors((prev) => ({ ...prev, otp: '' }));

        if (digit && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, event) => {
        if (event.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
            return;
        }

        if (event.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
            return;
        }

        if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (event) => {
        event.preventDefault();

        const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);

        if (!pasted) return;

        const nextOtp = pasted.split('');
        while (nextOtp.length < OTP_LENGTH) nextOtp.push('');

        setOtp(nextOtp);
        setError('');
        setFieldErrors((prev) => ({ ...prev, otp: '' }));
        inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setError('');
        setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const token = otp.join('');
        const password = form.password.trim();
        const passwordConfirmation = form.password_confirmation.trim();
        const normalizedLogin = normalizePhone(login || '');

        if (!login) {
            setError('Data akun reset tidak ditemukan. Silakan ulangi dari halaman lupa password.');
            return;
        }

        if (token.length !== OTP_LENGTH) {
            setFieldErrors((prev) => ({ ...prev, otp: 'Kode OTP harus 6 digit.' }));
            return;
        }

        if (!password) {
            setFieldErrors((prev) => ({ ...prev, password: 'Password baru wajib diisi.' }));
            return;
        }

        if (password.length < 8) {
            setFieldErrors((prev) => ({ ...prev, password: 'Password minimal 8 karakter.' }));
            return;
        }

        if (password !== passwordConfirmation) {
            setFieldErrors((prev) => ({ ...prev, password_confirmation: 'Konfirmasi password tidak sama.' }));
            return;
        }

        setLoading(true);
        setError('');
        setFieldErrors({
            otp: '',
            password: '',
            password_confirmation: '',
        });

        try {
            const response = await axiosInstance.post(
                '/auth/reset-password',
                {
                    login: login.includes('@') ? login : normalizedLogin,
                    token,
                    password,
                    password_confirmation: passwordConfirmation,
                },
                {
                    validateStatus: (status) => status < 500,
                }
            );

            const responseData = response?.data || {};

            if (!responseData?.success) {
                const apiMessage = responseData?.message || '';
                const backendErrors = responseData?.errors || {};

                if (response.status === 422) {
                    setFieldErrors({
                        otp: backendErrors.token?.[0] || '',
                        password: backendErrors.password?.[0] || '',
                        password_confirmation: backendErrors.password_confirmation?.[0] || '',
                    });
                    setError(apiMessage || 'Data reset password belum valid.');
                    return;
                }

                if (response.status === 404) {
                    setError(apiMessage || 'Akun tidak ditemukan.');
                    return;
                }

                if (response.status === 410) {
                    setError(apiMessage || 'Kode OTP sudah kedaluwarsa. Silakan minta kode baru.');
                    return;
                }

                if (response.status === 429) {
                    setError(apiMessage || 'Terlalu banyak percobaan. Coba lagi beberapa saat.');
                    return;
                }

                setError(apiMessage || 'Gagal mengubah password.');
                return;
            }

            sessionStorage.removeItem('mp_reset_login');
            sessionStorage.removeItem('mp_reset_channel');

            setSuccess(responseData?.message || 'Password berhasil diperbarui. Anda akan diarahkan ke login.');
            setTimeout(() => {
                navigate('/login', {
                    replace: true,
                    state: { message: 'Password berhasil diperbarui. Silakan login kembali.' },
                });
            }, 1600);
        } catch (err) {
            setError(err?.apiMessage || 'Terjadi kesalahan. Silakan coba lagi.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.page}>
            <section className={styles.card}>
                <div className={styles.header}>
                    <div className={styles.iconBox} aria-hidden="true">
                        <MdLockReset size={34} />
                    </div>

                    <div className={styles.logoWrapper}>
                        <img src="/logo-dark.png" alt="Logo" className={styles.logo} />
                    </div>

                    <span className={styles.badge}>Reset Password</span>
                    <h1 className={styles.title}>Verifikasi OTP</h1>
                    <p className={styles.subtitle}>
                        Masukkan kode OTP yang dikirim ke <strong>{maskLogin(login)}</strong>, lalu buat password baru.
                    </p>
                </div>

                {error && (
                    <div className={styles.errorBox} role="alert" aria-live="polite">
                        {error}
                    </div>
                )}

                {success && (
                    <div className={styles.successBox} role="status" aria-live="polite">
                        {success}
                    </div>
                )}

                <form onSubmit={handleSubmit} noValidate className={styles.form}>
                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="login-display">
                            Akun tujuan
                        </label>
                        <div className={styles.readOnlyBox}>
                            <MdPhone size={18} />
                            <span>{maskLogin(login)}</span>
                        </div>
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Kode OTP 6 Digit</label>
                        <div className={styles.otpGroup} onPaste={handlePaste}>
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(el) => {
                                        inputRefs.current[index] = el;
                                    }}
                                    className={`${styles.otpInput} ${digit ? styles.otpFilled : ''}`}
                                    type="tel"
                                    inputMode="numeric"
                                    pattern="\d*"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                    aria-label={`Digit OTP ${index + 1}`}
                                    disabled={loading}
                                />
                            ))}
                        </div>
                        {fieldErrors.otp && (
                            <span className={styles.fieldError} role="alert">
                                {fieldErrors.otp}
                            </span>
                        )}
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="password">
                            Password Baru
                        </label>

                        <div className={styles.inputWrap}>
                            <MdLock className={styles.iconLeft} size={18} />
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                className={`${styles.input} ${styles.inputPadRight} ${fieldErrors.password ? styles.inputError : ''}`}
                                placeholder="Masukkan password baru"
                                value={form.password}
                                onChange={handleChange}
                                autoComplete="new-password"
                                aria-invalid={!!fieldErrors.password}
                                aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                                disabled={loading}
                            />

                            <button
                                type="button"
                                className={styles.toggleBtn}
                                onClick={() => setShowPassword((v) => !v)}
                                aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                                disabled={loading}
                            >
                                {showPassword ? (
                                    <MdOutlineVisibilityOff size={19} />
                                ) : (
                                    <MdOutlineVisibility size={19} />
                                )}
                            </button>
                        </div>

                        {fieldErrors.password && (
                            <span id="password-error" className={styles.fieldError} role="alert">
                                {fieldErrors.password}
                            </span>
                        )}
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="password_confirmation">
                            Konfirmasi Password
                        </label>

                        <div className={styles.inputWrap}>
                            <MdLock className={styles.iconLeft} size={18} />
                            <input
                                id="password_confirmation"
                                type={showConfirmation ? 'text' : 'password'}
                                name="password_confirmation"
                                className={`${styles.input} ${styles.inputPadRight} ${
                                    fieldErrors.password_confirmation ? styles.inputError : ''
                                }`}
                                placeholder="Ulangi password baru"
                                value={form.password_confirmation}
                                onChange={handleChange}
                                autoComplete="new-password"
                                aria-invalid={!!fieldErrors.password_confirmation}
                                aria-describedby={
                                    fieldErrors.password_confirmation ? 'password-confirm-error' : undefined
                                }
                                disabled={loading}
                            />

                            <button
                                type="button"
                                className={styles.toggleBtn}
                                onClick={() => setShowConfirmation((v) => !v)}
                                aria-label={showConfirmation ? 'Sembunyikan password' : 'Tampilkan password'}
                                disabled={loading}
                            >
                                {showConfirmation ? (
                                    <MdOutlineVisibilityOff size={19} />
                                ) : (
                                    <MdOutlineVisibility size={19} />
                                )}
                            </button>
                        </div>

                        {fieldErrors.password_confirmation && (
                            <span id="password-confirm-error" className={styles.fieldError} role="alert">
                                {fieldErrors.password_confirmation}
                            </span>
                        )}
                    </div>

                    <button type="submit" className={styles.submitBtn} disabled={loading}>
                        {loading ? <span className={styles.spinner} aria-hidden="true" /> : 'Reset Password'}
                    </button>
                </form>

                <div className={styles.footer}>
                    <Link to="/forgot-password" className={styles.backLink}>
                        <MdArrowBack size={16} /> Kembali
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default ResetPassword;
